<template>
  <page back-to="/campaigns" :title="pageTitle" size="is-desktop">
    <div slot="page-header-right">

      <button v-if="isEdit" class="button is-outlined mr-2 is-danger" @click="confirmDestroy">Delete</button>

      <button
        class="button is-primary"
        @click="$refs.submitButton.click()"
      >{{ isEdit ? 'Update' : 'Create'}}</button>
    </div>

    <form @submit.prevent="save">
      <button ref="submitButton" type="submit" class="is-hidden"></button>

      <div class="columns">
        <div class="column is-4">
          <div class="box">
            <b-field label="Title">
              <b-input v-model="form.title"></b-input>
            </b-field>

            <b-field label="Start date">
              <datepicker v-model="form.start_date"></datepicker>
            </b-field>

            <b-field label="End date">
              <datepicker v-model="form.end_date"></datepicker>
            </b-field>

            <b-field label="Total price (₮)" v-if="campaign">
              <b-input v-model="form.total_price" readonly></b-input>
            </b-field>

            <b-field label="Price (₮)" v-if="campaign">
              <b-input v-model="form.price"></b-input>
            </b-field>
          </div>
          <div class="box">
            <div class="level">
              <div class="level-left">
                Campaign Cover
              </div>

              <div class="level-right">
                <upload-file
                  ref="uploadFile"
                  :endpoint="$store.getters.kytebaseMediaUploadUrl"
                  :additional="{
                    collection: 'images'
                  }"
                  @uploaded="onCoverUploaded"
                  auto-reset
                >
                  <template slot-scope="{progress}">
                    <div
                      v-if="progress && ! progress.isComplete()"
                    >Uploading {{ progress.percent() }}%</div>

                    <image-picker
                      v-else
                      @picked="file => $refs.uploadFile.upload(file)"
                      croppable
                      :croppie="{ viewMode: 1, output: 'image/png'}"
                    >
                      <a slot="trigger">Upload</a>
                    </image-picker>
                  </template>
                </upload-file>
              </div>
            </div>

            <div class="has-text-centered">
              <img v-if="cover_image_url" :src="cover_image_url" style="width: 150px;" />
            </div>
          </div>
        </div>

        <div class="column is-8">
          <div class="box" v-if="campaign">
            <div class="level">
              <div class="level-left">
                <p class="title is-6">Issues</p>
              </div>

              <div class="level-right" >
                <b-dropdown position="is-bottom-left">
                  <a slot="trigger">Add</a>

                  <div class="dropdown-item" style="min-width: 300px;">
                    <campaign-issue-modal :campaign-id="campaign.id" @added="onAddIssue"></campaign-issue-modal>
                  </div>
                </b-dropdown>
              </div>
            </div>

            <b-table
              class="is-middle"
              :data="issues"
            >
              <template slot-scope="props">
                <b-table-column label="Issue">
                  <div class="media">
                    <div class="media-left">
                      <img :src="props.row.cover_image_url" width="50"/>
                    </div>

                    <div class="media-content">
                      {{props.row.title}} / {{props.row.magazine.name}}
                    </div>
                  </div>
                </b-table-column>
                <b-table-column label="Price" class="text-right">
                  {{props.row.price}}
                </b-table-column>

                <b-table-column custom-key="actions">
                  <div class="is-pulled-right">
                    <button type="button" class="button is-white" @click="removeIssue(props.row)">
                      <b-icon icon="close" size="is-small"></b-icon>
                    </button>
                  </div>
                </b-table-column>
              </template>
              <template slot="empty">
                <section class="section">
                  <div class="content has-text-grey has-text-centered">
                    <p>
                      <b-icon icon="emoticon-sad" size="is-large"></b-icon>
                    </p>
                    <p>{{$tc('Nothinghere')}}</p>
                  </div>
                </section>
              </template>
            </b-table>
          </div>
          <div v-else class="border border-t-0 border-red-400 rounded-b bg-blue px-4 py-3 text-red-700">
              Please create a campaign in order to add issues to the campaign.
            </div>
        </div>
      </div>
    </form>
  </page>
</template>

<script>
import ImagePicker from "@/components/ImagePicker";
import UploadFile from "@/components/UploadFile";
import CampaignIssueModal from "./components/CampaignIssueModal";

export default {
  components: {
    ImagePicker,
    UploadFile,
    CampaignIssueModal
  },

  props: {
    campaign: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      cover_image_url: this.campaign ? this.campaign.cover_image_url : null,

      form: new window.Form({
        title: this.campaign ? this.campaign.title : null,
        price: this.campaign ? this.campaign.price : 0,
        total_price: this.campaign ? parseFloat(this.campaign.total_price) : 0,
        start_date: this.campaign ? this.campaign.start_date : null,
        end_date: this.campaign ? this.campaign.end_date : null,
        cover_image_path: this.campaign ? this.campaign.cover_image_path : null,
      }),
      issues: this.campaign ? this.campaign.issues : []
    };
  },

  computed: {
    isEdit() {
      return this.campaign != null;
    },

    pageTitle() {
      if (this.isEdit) {
        return "Campaigns / Edit #" + this.campaign.id;
      }

      return "Campaigns / Create";
    }
  },

  methods: {
    save() {
      this.isEdit ? this.update() : this.create();
    },

    create() {
      this.form.post(`/admin/api/magazine/campaigns`).then( (resp) => {
        this.$router.push("/campaigns/"+resp.data.id+"/edit");
        this.$buefy.toast.open("Campaign created. Now you can add issues to this campaign.");
      });
    },

    update() {
      this.form
        .put(`/admin/api/magazine/campaigns/${this.campaign.id}`)
        .then(this.whenSaved);
    },

    whenSaved() {
      this.$buefy.toast.open("Campaign saved.");
      this.$router.push("/campaigns");
    },

    onCoverUploaded({ data: image }) {
      this.form.cover_image_path = image.path;
      this.cover_image_url = image.url;
      this.form.cover_sizes = image.cover_sizes
    },

    confirmDestroy() {
      this.$buefy.dialog.confirm({
        title: "Deleting campaign",
        message:
          "Are you sure you want to <b>delete</b> this campaign? This action cannot be undone.",
        confirmText: "Delete Campaign",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.destroy()
      });
    },

    destroy() {
      let loading = this.$buefy.loading.open();

      new window.Form()
        .delete(`/admin/api/magazine/campaigns/${this.campaign.id}`)
        .then(() => {
          this.$buefy.toast.open("Campaign deleted.");

          loading.close();

          this.$router.push("/campaigns");
        })
        .catch(() => loading.close());
    },

    onAddIssue(data) {
      this.issues.push(data);
      this.form.total_price = (parseFloat(this.form.total_price) + parseFloat(data.price)).toFixed(2);
      this.$buefy.toast.open("The issue added.");
    },

    removeIssue(data) {
      this.$http
        .delete(
          "/admin/api/magazine/campaigns/" +
          this.campaign.id + "/issue/" + data.id
        );
      this.form.total_price = (parseFloat(this.form.total_price) - parseFloat(data.price)).toFixed(2);
      this.issues.splice(this.issues.indexOf(data), 1);
      this.$buefy.toast.open("The issue removed.");
    },

    close() {

    }
  }
};
</script>
